import React from 'react';
import {
  LinkField,
  RichTextField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ButtonProps, SitecoreImage } from '@alliander-fe/sitecore-types';
import { IntegrationApiFeaturesCongestionReportsContractsResponsesGetCongestionReportsDetailsResponse as CongestionReportDetails } from '@alliander-fe/api';
import { useSitecoreContext } from '@liander/context';
import { ComponentProps } from 'lib/component-props';
import {
  FilterValuePropertyFromFields,
  mapWithoutValueProperty,
} from 'src/utils/mapWithoutValueProperty';
import { CongestionReportHero as CongestionReportHeroComponent } from 'src/components/CongestionReportHero';
import { getFilteredTopTasks } from 'src/utils/getFilteredTopTasks';

export type TasksProps = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    title: { value: string };
    color: { value: string };
    link: { value: ButtonProps };
  };
};

export type EnrichedCongestionReportHeroProps = {
  fields: {
    isLoading?: { value: boolean };
    isError?: { value: boolean };
    congestionReport?: { value: CongestionReportDetails };
  };
};

export type CongestionReportHeroProps = {
  fields: {
    title: TextField;
    tasks: TasksProps[];
    subTitle: TextField;
    statusPhaseOneLabel?: TextField;
    statusPhaseOneText?: RichTextField;
    statusPhaseTwoLabel?: TextField;
    statusPhaseTwoText?: RichTextField;
    statusPhaseThreeLabel?: TextField;
    statusPhaseThreeText?: RichTextField;
    statusLabel?: TextField;
    congestionTypeLabel?: TextField;
    image?: { value?: SitecoreImage };
    errorTitle?: TextField;
    errorText?: RichTextField;
    notFoundTitle?: TextField;
    notFoundText?: RichTextField;
    detailsModalTitle?: TextField;
    detailsModalTextLink?: LinkField;
    detailsModalButtonLabel?: TextField;

    enableResolvedDateRange: { value: boolean };
  };
} & ComponentProps;

type CombinedCongestionReportHeroProps = CongestionReportHeroProps &
  EnrichedCongestionReportHeroProps;

export type FilteredCongestionReportHeroProps = FilterValuePropertyFromFields<
  CombinedCongestionReportHeroProps['fields']
>;

export type TopTasks = ReturnType<typeof getFilteredTopTasks>;

const CongestionReportsHero = (props: CombinedCongestionReportHeroProps) => {
  const { isEditor } = useSitecoreContext();

  if (!props.fields) return null;

  // Add mock data to the props if the component is in editor mode.
  const propsWithMockData = isEditor
    ? {
        ...props,
        fields: {
          ...props.fields,
          isLoading: { value: false },
          isError: { value: false },
          congestionReport: { value: congestionReportDetailsMockData },
        },
      }
    : props;

  const fields = mapWithoutValueProperty(propsWithMockData.fields);
  const topTasks = getFilteredTopTasks(propsWithMockData.fields?.tasks);

  return <CongestionReportHeroComponent {...fields} topTasks={topTasks ?? []} />;
};

export default withDatasourceCheck()<CombinedCongestionReportHeroProps>(CongestionReportsHero);

// We added the mock data so we can see the component correctly in the editor.
const congestionReportDetailsMockData: CongestionReportDetails = {
  id: '919B81E6-7243-47D5-A322-4BBCD6282DCA',
  name: 'SS BIDDINGHUIZEN 10-1i',
  locations: 'Biddinghuizen',
  congestionType: 'Verbruik',
  status: 'Voltooid',
};
